/**
 * @file MultiSelectDropdown.js
 * @version 1.8.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-30
 * @modified 2026-10-16
 * @description Multi-select dropdown. Pass `searchable` to opt in to a
 *              search input at the top of the menu — filters options by
 *              their `searchText` (preferred) or `label` string. Pass
 *              `summarize(value, options)` to fully control the trigger text
 *              and `hideTags` to suppress the removable chip list below the
 *              trigger (e.g. when used inline in a filter-row grid cell).
 *
 *              Pass `groups` to section the option list under headers, each
 *              with a select-all-in-group tick. Options then carry a `group`
 *              matching a group's `key`. When `groups` is omitted the render
 *              path is unchanged.
 *
 *              Pass `tickBoxes` to draw each option's tick with the shared
 *              TickCheckbox instead of the round check badge (Manage
 *              Inventory's account filters). Off by default, so every other
 *              caller renders as before.
 */

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import './MultiSelectDropdown.css';
import useDropdownPosition from '../hooks/useDropdownPosition';
import useDropdownKeys from '../hooks/useDropdownKeys';
import useRepMobile from '../hooks/useRepMobile';
import useScrollAffordance from '../hooks/useScrollAffordance';
import TickCheckbox from './TickCheckbox';
import ScrollMore from './ScrollMore';

function MultiSelectDropdown({
  options,
  value = [],
  onChange,
  placeholder = "Select...",
  emptyMessage = "No options available",
  searchable = false,
  searchPlaceholder = "Search…",
  disabled = false,
  // When true, the open menu shows the currently-selected items in a list at
  // the top (each removable on click) with the full, scrollable options list
  // beneath — instead of rendering removable tag chips below the trigger.
  selectedInMenu = false,
  // Optional (value, options) => string that fully controls the trigger label,
  // overriding the default empty/one/"N selected" logic.
  summarize = null,
  // Suppress the removable chip list rendered below the trigger. Useful when
  // the dropdown sits in a fixed-height filter-row grid cell.
  hideTags = false,
  // Re-evaluate flip direction as the scroll ancestor scrolls, and flip up only
  // when the menu would actually overflow that ancestor's bottom edge (opt-in;
  // used for dropdowns inside a scrolling table).
  reflowOnScroll = false,
  // Measure against a named ancestor rather than the nearest scroll parent, and
  // take the menu out of the clipping ancestor so it can use that space. A card
  // on the dashboard is short: without these the menu decides there is no room
  // either way and opens into a few pixels. SearchableDropdown has carried both
  // for a while; this is the same pass-through.
  boundarySelector = null,
  escapeOverflow = false,
  // 'left' | 'right' — pin the escaped menu to that edge of widthSelector's
  // element, sized to it. For a narrow filter whose options are wider than it.
  align = null,
  widthSelector = null,
  // Optional [{ key, label }] that sections the option list under headers, each
  // with a select-all-in-group tick. Options opt in via `group: <key>`. Omit
  // for the flat list every existing caller uses.
  groups = null,
  // Draw each option's tick with TickCheckbox rather than the round check
  // badge. Off by default — existing callers keep their look.
  tickBoxes = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  // For the TickCheckbox ids under `tickBoxes` (two dropdowns on one screen
  // must not share an input id).
  const instanceId = useId();
  const [searchTerm, setSearchTerm] = useState('');
  // The keyboard cursor, separate from the selection. See hooks/useDropdownKeys.
  const [cursor, setCursor] = useState(0);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const optionsRef = useRef(null);
  /*
   * On the phone the menu is a sheet covering most of the screen (RepMobile.css)
   * and behaves differently in two ways that cannot be expressed in CSS.
   *
   * Read from the body attribute rather than taken as a prop, exactly as
   * useReadOnly does: the rule is "REP badge AND narrow", every searchable
   * dropdown in the app is subject to it, and a prop is something each of the
   * forty call sites would have to remember to pass.
   */
  const repMobile = useRepMobile();
  // How much of the option list is still out of sight, for the cues below.
  const [scrollRef, scroll] = useScrollAffordance([isOpen, searchTerm, options?.length]);
  /* One element, two refs: the keyboard cursor scrolls itself into view through
     optionsRef, and the affordance measures the same box. */
  const setOptionsEl = useCallback((el) => {
    optionsRef.current = el;
    scrollRef.current = el;
  }, [scrollRef]);
  const { flipUp, theHeight, menuStyle } = useDropdownPosition(
    dropdownRef, isOpen, { reflowOnScroll, boundarySelector, escapeOverflow, align, widthSelector });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (optionValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  // Filter options by the search term — match against `searchText`
  // (typed-in friendly representation) or stringified `label`. Lifted out of
  // the render so the keyboard handler can see the same list the menu draws.
  const term = searchTerm.trim().toLowerCase();
  const filtered = !searchable || term === ''
    ? options
    : options.filter(o => {
        const haystack = `${o.searchText || ''} ${typeof o.label === 'string' ? o.label : ''}`.toLowerCase();
        return haystack.includes(term);
      });

  // The options in the order they are DRAWN. Grouped, that is each group's
  // matches in turn and then anything in no known group — not `filtered`'s
  // order, and arrowing through `filtered` would make the cursor jump about.
  // Group headers are left out: their select-all is a mouse affordance, and
  // Enter on an option already covers the keyboard.
  const navigable = (() => {
    if (!groups) return filtered;
    const known = new Set(groups.map(g => g.key));
    return [
      ...groups.flatMap(g => filtered.filter(o => o.group === g.key)),
      ...filtered.filter(o => !known.has(o.group)),
    ];
  })();
  const navIndex = new Map(navigable.map((o, i) => [o.value, i]));

  useEffect(() => { setCursor(0); }, [searchTerm]);

  const { onTriggerKeyDown, onMenuKeyDown } = useDropdownKeys({
    isOpen,
    setIsOpen,
    navigable,
    cursor,
    setCursor,
    onChoose: (option) => handleToggle(option.value),
    // Enter toggles and the menu stays open: picking several is the point.
    multi: true,
    triggerRef,
    listRef: optionsRef,
    onType: searchable ? (ch) => setSearchTerm(ch) : undefined,
    // With no search box focus stays on the trigger, where Space is free.
    spaceChooses: !searchable,
  });

  const getDisplayText = () => {
    if (typeof summarize === 'function') {
      return summarize(value, options);
    }
    if (!value || value.length === 0) {
      return placeholder;
    }
    if (value.length === 1) {
      const opt = options.find(o => o.value === value[0]);
      return opt?.label || value[0];
    }
    return `${value.length} selected`;
  };

  return (
    <div className={`multi-select-dropdown ${flipUp ? 'flip-up' : ''}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        className={`dropdown-trigger ${value.length > 0 ? 'has-selection' : ''}`}
        onClick={() => {
          if (disabled) return;
          if (!isOpen) setCursor(0);
          setIsOpen(!isOpen);
        }}
        // Open, a searchable menu has moved focus into its search box, so this
        // only ever sees the open keys when there is no search box.
        onKeyDown={disabled ? undefined : (isOpen ? onMenuKeyDown : onTriggerKeyDown)}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="trigger-text">{getDisplayText()}</span>
        <span className="material-symbols-rounded dropdown-arrow">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isOpen && (() => {
        const renderOption = (option) => (
          <div
            key={option.value}
            className={`dropdown-item ${value.includes(option.value) ? 'selected' : ''} ${cursor === navIndex.get(option.value) ? 'is-cursor' : ''}`}
            data-cursor={cursor === navIndex.get(option.value) ? 'true' : undefined}
            onMouseEnter={() => setCursor(navIndex.get(option.value) ?? 0)}
            onClick={() => handleToggle(option.value)}
          >
            {tickBoxes ? (
              /* Stop propagation so a click on the tick itself is not also the
                 row's click — the two would toggle and cancel out (as the group
                 head does). */
              <span className="tick-box" onClick={(e) => e.stopPropagation()}>
                <TickCheckbox
                  id={`msd-${instanceId}-${option.value}`}
                  checked={value.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  size={14}
                  ariaLabel={typeof option.label === 'string' ? option.label : String(option.searchText || option.value)}
                />
              </span>
            ) : (
              <span className="checkbox">
                {value.includes(option.value) && (
                  <span className="material-symbols-rounded">check</span>
                )}
              </span>
            )}
            <span className="item-label">{option.label}</span>
            {option.count !== undefined && (
              <span className="item-count">({option.count})</span>
            )}
          </div>
        );

        // Grouped list: partition the already-filtered options so a group with
        // no search matches renders no header at all. The header row must NOT
        // carry `dropdown-item` or it inherits the click-to-toggle handler.
        const renderGrouped = () => {
          const known = new Set(groups.map(g => g.key));
          // An option whose `group` matches no declared group would otherwise
          // render nowhere at all — list those plainly at the end rather than
          // dropping them silently.
          const ungrouped = filtered.filter(o => !known.has(o.group));

          const sections = groups.map(group => {
          const groupOptions = filtered.filter(o => o.group === group.key);
          if (groupOptions.length === 0) return null;

          const groupValues = groupOptions.map(o => o.value);
          const selectedCount = groupValues.filter(v => value.includes(v)).length;
          const allSelected = selectedCount === groupValues.length;
          const partial = selectedCount > 0 && !allSelected;

          const toggleGroup = () => {
            if (allSelected) {
              onChange(value.filter(v => !groupValues.includes(v)));
            } else {
              onChange([...value, ...groupValues.filter(v => !value.includes(v))]);
            }
          };

          return (
            <div className="dropdown-group" key={group.key}>
              <div
                className={`dropdown-group-head${partial ? ' partial' : ''}`}
                onClick={toggleGroup}
              >
                {/* Stop propagation so a click on the tick doesn't also fire
                    the row's onClick and cancel itself out. */}
                <span onClick={(e) => e.stopPropagation()}>
                  <TickCheckbox
                    id={`msd-group-${group.key}`}
                    checked={allSelected}
                    onChange={toggleGroup}
                    size={14}
                    ariaLabel={`Select all ${group.label}`}
                  />
                </span>
                <span className="group-label">{group.label}</span>
                <span className="group-count">{selectedCount}/{groupValues.length}</span>
              </div>
              {groupOptions.map(renderOption)}
            </div>
          );
          });

          return [...sections, ...ungrouped.map(renderOption)];
        };

        const optionList = filtered.length === 0 ? (
          <div className="dropdown-empty">{emptyMessage}</div>
        ) : (
          groups ? renderGrouped() : filtered.map(renderOption)
        );
        return (
          <div className={`dropdown-menu${selectedInMenu ? ' has-selected-section' : ''}`}
               style={{ ...(theHeight ? { height: `${theHeight}px` } : null), ...(menuStyle || null) }}>
            {/* The way out.

                The sheet used to leave a strip of page showing at the top to
                tap on; at 20px from the top there is no strip left, so the
                dismiss has to be part of the sheet. Full width and transparent
                so it reads as the head of the sheet rather than a control — the
                icon says what it does, and the whole bar takes the tap, which
                is the biggest target on the screen that isn't an option. */}
            {repMobile && (
              <button
                type="button"
                className="dropdown-sheet-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
            {searchable && (
              <div className="dropdown-search">
                <span className="material-symbols-rounded">search</span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={onMenuKeyDown}
                  placeholder={searchPlaceholder}
                  /* Not on a phone. Focusing the field on open throws the
                     keyboard up over the options before the list has been
                     looked at, and the tap that opened the menu becomes a
                     commitment to typing. The field is still there to be
                     tapped; it just waits to be asked. */
                  autoFocus={!repMobile}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {selectedInMenu && value.length > 0 && (
              <div className="dropdown-selected-section">
                <div className="dropdown-selected-head">Selected ({value.length})</div>
                <div className="dropdown-selected-list">
                  {value.map(v => {
                    const opt = options.find(o => o.value === v);
                    return (
                      <div key={v} className="dropdown-selected-chip" onClick={() => handleToggle(v)} title="Remove">
                        <span className="sel-label">{opt?.label ?? v}</span>
                        <span className="material-symbols-rounded">close</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Always in a scroll container, not only in the selectedInMenu
                layout. Without it the checkbox list rendered straight into the
                menu, which sets its own height — so a long list was clipped
                with nothing to scroll. */}
            {repMobile ? (
              /* The sheet fills the screen, so there is no edge of a menu to
                 tell you the list carries on past it — and with the keyboard up
                 there is very little of it showing. The wrapper must sit OUTSIDE
                 the scrolling box or the cue travels with the content; see
                 ScrollMore.css. Phone only: on a desktop the menu is small
                 enough to read the end of, and every existing caller keeps its
                 markup unchanged. */
              <div className="scroll-more-wrap">
                <div className="dropdown-options scroll-more-box" ref={setOptionsEl}>{optionList}</div>
                <ScrollMore
                  scrollRef={optionsRef}
                  direction="up"
                  remaining={scroll.above ? 1 - scroll.remaining : 0}
                />
                <ScrollMore scrollRef={optionsRef} remaining={scroll.remaining} />
              </div>
            ) : (
              <div className="dropdown-options" ref={setOptionsEl}>{optionList}</div>
            )}
          </div>
        );
      })()}

      {!selectedInMenu && !hideTags && value.length > 0 && (
        <div className="selected-tags">
          {value.map(v => {
            const opt = options.find(o => o.value === v);
            return (
              <span key={v} className="tag">
                {opt?.label || v}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(v);
                  }}
                >
                  <span className="material-symbols-rounded">close</span>
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;
